const selectors = {
  deck: '[data-voices-deck]',
  glow: '[data-voices-glow]',
  item: '[data-flick-cards-item]',
  counter: '[data-slider-fraction]',
  control: '[data-slider-nav]',
  tabs: '[data-toggle-init]'
};

const minCardsForDrag = 7;
const deckApis = new WeakMap();
const tabApis = new WeakMap();
const fractionApis = new WeakMap();

/**
 * The Osmo slider owns the deck, so the counter reads the status attribute it
 * writes on every card. That keeps drag, arrows and taps reporting the same
 * active index without touching the slider's own logic.
 */

function watchActiveCard(deck, onChange) {
  const items = Array.from(deck.querySelectorAll(selectors.item));

  const observer = new MutationObserver(() => {
    const index = items.findIndex(item => item.dataset.flickCardsItemStatus === 'active');

    if (index !== -1) {
      onChange(index, items[index]);
    }
  });

  items.forEach(item => observer.observe(item, { attributes: true, attributeFilter: ['data-flick-cards-item-status'] }));

  return {
    items,
    total: items.length,
    getActiveIndex: () => items.findIndex(item => item.dataset.flickCardsItemStatus === 'active')
  };
}

/**
 * Cards are never removed: picking a group walks to its next card instead, so
 * the deck keeps the seven cards the slider needs for dragging.
 */

function findNextInGroup(items, group, fromIndex) {
  for (let step = 1; step <= items.length; step += 1) {
    const index = (fromIndex + step + items.length) % items.length;

    if (items[index].dataset.voiceGroup === group) {
      return index;
    }
  }

  return -1;
}


// ─── Glow ─────────────────────────────────────────────────────────────────────

const glowScale = 0.02;

const glows = [
  { x: 0.5, y: 1, radius: 0.5, alpha: 0.2 },
  { x: 0.5, y: 1.06, radius: 1, alpha: 0.14 }
];

/**
 * A two-stop radial gradient shows a hard edge and banding, so the falloff is
 * sampled from exp(-k·t²) across twelve stops — the visual equivalent of a very
 * large blur. The canvas itself is drawn at 2% of its box and stretched by CSS,
 * which smooths whatever is left.
 */

function gaussianStops(alpha, steps = 12, k = 3.2) {
  return Array.from({ length: steps + 1 }, (_, step) => {
    const t = step / steps;
    const value = step === steps ? 0 : alpha * Math.exp(-k * t * t);

    return [t, `oklch(90% 0.14 95 / ${value.toFixed(3)})`];
  });
}

function initGlow(canvas) {
  const context = canvas.getContext('2d');

  function draw() {
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    if (!width || !height) {
      return;
    }

    canvas.width = Math.max(2, Math.round(width * glowScale));
    canvas.height = Math.max(2, Math.round(height * glowScale));
    context.clearRect(0, 0, canvas.width, canvas.height);

    glows.forEach(({ x, y, radius, alpha }) => {
      const centerX = x * canvas.width;
      const centerY = y * canvas.height;
      const centerRadius = radius * canvas.width;
      const gradient = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, centerRadius);

      gaussianStops(alpha).forEach(([offset, color]) => gradient.addColorStop(offset, color));

      context.fillStyle = gradient;
      context.fillRect(0, 0, canvas.width, canvas.height);
    });
  }

  draw();

  new ResizeObserver(draw).observe(canvas);
}

function initVoices() {
  const decks = document.querySelectorAll(selectors.deck);

  decks.forEach(deck => {
    const section = deck.closest('.voices');
    const counter = section?.querySelector(selectors.counter);
    const controls = section?.querySelectorAll(selectors.control) ?? [];
    const tabs = section?.querySelector(selectors.tabs);
    const glow = section?.querySelector(selectors.glow);

    if (glow) {
      initGlow(glow);
    }

    const { items, total, getActiveIndex } = watchActiveCard(deck, (index, item) => {
      fractionApis.get(counter)?.setValue(index + 1);

      const tabApi = tabs && tabApis.get(tabs);

      if (!tabApi) {
        return;
      }

      const groupIndex = tabApi.values.indexOf(item.dataset.voiceGroup);

      if (groupIndex !== -1) {
        tabApi.setActive(groupIndex);
      }
    });

    controls.forEach(control => {
      if (total < minCardsForDrag) {
        control.disabled = true;

        return;
      }

      control.addEventListener('click', () => {
        const api = deckApis.get(deck);

        if (!api) {
          return;
        }

        const step = control.dataset.sliderNav === 'prev' ? -1 : 1;
        const current = getActiveIndex();

        api.goTo((current === -1 ? 0 : current) + step);
      });
    });

    tabs?.addEventListener('tabs:change', event => {
      const api = deckApis.get(deck);

      if (!api) {
        return;
      }

      const next = findNextInGroup(items, event.detail.value, getActiveIndex());

      if (next !== -1) {
        api.goTo(next);
      }
    });
  });
}

document.addEventListener('flick-cards:ready', event => {
  deckApis.set(event.target, event.detail);
});

document.addEventListener('tabs:ready', event => {
  tabApis.set(event.target, event.detail);
});

document.addEventListener('slider-fraction:ready', event => {
  fractionApis.set(event.target, event.detail);
});

initVoices();
